import { createContext, useMemo, useRef, useState } from "react";
const VIEW_MODE_MAP = {
    0: 'parallel',
    1: 'compare',
    2: 'single',
    3: 'multiple',
}

export const PictureViewerContext = createContext()

/**
 * PictureViewer 上下文提供者。
 *
 * 该 Provider 主要负责托管预览器的内部运行状态，通常不单独使用，
 * 建议通过 `PictureViewer` 默认导出组件直接接入。
 *
 * 初始化参数说明：
 * - `defaultViewMode`: 初始视图模式，`0=parallel`、`1=compare`、`2=single`、`3=multiple`
 * - `defaultImages`: 初始图片列表，结构见 `PictureViewer/index.jsx` 中的注释
 * - `defaultIndex`: 初始主图索引
 * - `allowStatusDeselect`: 是否允许取消当前状态
 * - `floatingLabelToolVisibleStatuses`: 哪些状态下展示浮动标注工具
 * - `floatingLabelToolPlacement`: 浮动标注工具位置，支持 `center` / `right`
 * - `canvasToolsDisabled`: 是否禁用画布标注工具
 */
export const PictureViewerContextProvider = ({ children, ...restProps }) => {
    const {
        defaultViewMode = 0,
        defaultImages = [],
        defaultIndex = 0,
        // 控制是否允许取消当前状态
        allowStatusDeselect = true,
        // 显示浮动标签工具的状态
        floatingLabelToolVisibleStatuses = ['rejected'],
        // 浮动标签工具的位置
        floatingLabelToolPlacement = 'center',
        // 是否禁用画布工具
        canvasToolsDisabled = false,
    } = restProps

    // 外部控制开关参数
    const [controls] = useState({
        allowStatusDeselect, // 控制是否允许取消当前状态
        floatingLabelToolVisibleStatuses, // 显示浮动标签工具的状态
        floatingLabelToolPlacement, // 浮动标签工具的位置
        canvasToolsDisabled, // 是否禁用画布工具
    })

    // 图片列表
    const [images, setImages] = useState(defaultImages)
    const [currentIndex, setCurrentIndex] = useState(defaultIndex)
    const imagesRef = useRef(defaultImages)
    const currentIndexRef = useRef(defaultIndex)
    // 多选槽槽位
    const [multipleSlots, setMultipleSlots] = useState([])
    const multipleSlotsRef = useRef([])
    // 视图
    const [viewMode, setViewMode] = useState(VIEW_MODE_MAP[defaultViewMode] || 'parallel')
    // SyncLock 状态
    const [parallelSyncLock, setParallelSyncLock] = useState(false)
    const [multipleSyncLocks, setMultipleSyncLocks] = useState([false, false])
    // 当前图片
    const currentImage = useMemo(() => images[currentIndex] || {}, [images, currentIndex])
    // 是否有下一张图片
    const hasNext = useMemo(() => currentIndex < images.length - 1, [currentIndex, images])
    // 是否有上一张图片
    const hasPrev = useMemo(() => currentIndex > 0, [currentIndex])

    return (
        <PictureViewerContext.Provider value={{
            images, setImages, imagesRef,
            currentIndex, setCurrentIndex, currentIndexRef,
            multipleSlots, setMultipleSlots, multipleSlotsRef,
            viewMode, setViewMode,
            parallelSyncLock, setParallelSyncLock,
            multipleSyncLocks, setMultipleSyncLocks,
            currentImage, hasNext, hasPrev,
            controls,
        }}>
            {children}
        </PictureViewerContext.Provider>
    )
}
